/* eslint-disable react-hooks/rules-of-hooks */
import { observer } from "mobx-react";
import {
  createStyles,
  Flex,
  Text,
  Card,
  Box,
} from "@mantine/core";
import { useStore } from "../../../../hooks/use-store";
import { getLoraOptons } from "../../../../types";

// Import car thumbnail images
import BMW_X3_Thumb from './Car_Lora_Thumb_BMW_X3.jpg';
import Genesis_Thumb from './Car_Lora_Thumb_Genesis.jpg';
import MB_EQS_Thumb from './Car_Lora_Thumb_MB_EQS.jpg';
import VW_Tiguan_Thumb from './Car_Lora_Thumb_VW_Tiguan.jpg';
import Toyota_RAV4_Thumb from './Car_Lora_Thumb_Toyota_RAV4.jpg';
import Toyota_RAV4_Thumb_2 from './RAV4New_Car_Lora_Thumb.jpg';
import Gwagon from './Car_Lora_Thumb_G-Wagon.jpg';
import q8 from './Car_Lora_ThumbAudiQ8.jpg';
// If you have an e-tron thumbnail, import it here. Otherwise it will show No Preview
// import etron from './Car_Lora_Thumb_Etron.jpg'
import RX from './RX_Car_Lora_Thumb.jpg'
import RZ from './RZ_Car_Lora_Thumb.jpg'
import ES from './ES_Car_Lora_Thumb.jpg'
import LM from './LM_Car_Lora_Thumb.jpg'

import Audi_Q5_Car_Lora_Thumb from './Audi_Q5_Car_Lora_Thumb.jpg'
import Audi_EtronGT_Car_Lora_Thumb from './Audi_EtronGT_Car_Lora_Thumb.jpg'
// import Panamera from './Porsche_Car_Lora_Thumb.jpg'

export type GalleryItem = {
  images: { url: string, preview: string }[];
  previewHdrl: string;
  enhancedHdrlFile: string;
  modelVersion?: string;
  loraName?: string;
}

const useStyles = createStyles((theme) => ({
  wrapper: {
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 20px',
    width: '100%',
    backgroundColor: theme.colorScheme === 'dark' ? '#1a1a1a' : '#f8f9fa',
    // minHeight: '100vh',
  },
  title: {
    fontSize: '32px',
    fontWeight: 400,
    color: theme.colorScheme === 'dark' ? '#ffffff' : '#000000',
    marginBottom: '30px',
    textAlign: 'center',
  },
  carGrid: {
    gap: '15px',
    marginBottom: '30px',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  carCard: {
    width: '220px',
    height: '260px',
    backgroundColor: theme.colorScheme === 'dark' ? '#2a2a2a' : '#ffffff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
    },
  },
  carImage: {
    width: '100%',
    height: '180px',
    objectFit: 'cover',
    borderRadius: '8px 8px 8px 8px',
  },
  carLabel: {
    fontSize: '14px',
    fontWeight: 500,
    textAlign: 'center',
    padding: '15px 10px',
    color: theme.colorScheme === 'dark' ? '#ffffff' : '#000000',
  },
  trainCard: {
    width: '220px',
    height: '260px',
    backgroundColor: 'transparent',
    border: `2px dashed ${theme.colorScheme === 'dark' ? '#666' : '#ccc'}`,
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'border-color 0.2s ease',
    '&:hover': {
      borderColor: '#f53c02',
    },
  },
  plusIcon: {
    width: '40px',
    height: '40px',
    backgroundColor: '#f53c02',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '15px',
    fontSize: '24px',
    color: 'white',
    fontWeight: 'bold',
  },
  trainText: {
    fontSize: '14px',
    fontWeight: 500,
    color: theme.colorScheme === 'dark' ? '#ffffff' : '#000000',
    textAlign: 'center',
  },
}));

// Map lora values to images and labels
const carImageMap: Record<string, { image: string; label: string; available: boolean }> = {
  'u_tiguan': {
    image: VW_Tiguan_Thumb,
    label: 'VW Tiguan (Demo Car)',
    available: true
  },
  'mag_bmw': {
    image: BMW_X3_Thumb,
    label: 'BMW (not yet available)',
    available: false
  },
  'mag_eqs': {
    image: MB_EQS_Thumb,
    label: 'Mercedes EQS (not yet available)',
    available: false
  },
  'mag_gen_v5': {
    image: Genesis_Thumb,
    label: 'Genesis',
    available: true
  },
  // Add fallback mappings for other possible values
  'mag_gen': {
    image: Genesis_Thumb,
    label: 'Genesis Latest',
    available: true
  },
  'mag_rv4_v1': {
    image: Toyota_RAV4_Thumb,
    label: 'Toyota RAV4',
    available: true
  },
  'mag_rv4_v2': {
    image: Toyota_RAV4_Thumb_2,
    label: 'Toyota RAV4 (August 2025)',
    available: true
  },
  // 'mag_rv4_un': {
  //   image: Toyota_RAV4_Thumb,
  //   label: 'Toyota RAV4 UN',
  //   available: true
  // },
  // { value: 'mag_audi_r8_rs', label: 'Audi R8 RS' },
  // { value: 'mag_mercedes_g63', label: 'Mercedes G63' },
  'mag_mercedes_g63': {
    image: Gwagon,
    label: 'Mercedes G63',
    available: true
  },
  'mag_audi_r8_rs': {
    image: q8,
    label: 'Audi Q8 RS',
    available: true
  },
  'mag_etron_2025': {
    image: Audi_EtronGT_Car_Lora_Thumb, // replace with etron when available
    label: 'Audi E-tron 2025',
    available: true
  },
  'mag_q5_2025': {
    image: Audi_Q5_Car_Lora_Thumb, // replace with etron when available
    label: 'Audi Q5 2025',
    available: true
  },
  'mag_lexus_rx': {
    image: RX,
    label: 'Lexus RX',
    available: true
  },
  'mag_lexus_rz': {
    image: RZ,
    label: 'Lexus RZ',
    available: true
  },
  'mag_lexus_lm': {
    image: LM, // Using RX thumbnail as placeholder for now
    label: 'Lexus LM',
    available: true
  },
  'mag_lexus_es': {
    image: ES, // Using RX thumbnail as placeholder for now
    label: 'Lexus ES',
    available: true
  },
  // 'mag_panamera': {
  //   image: Panamera,
  //   label: 'Porsche Panamera',
  //   available: true
  // },
  // 'mag_audi_r8_rs': {
  //   image: Gwagon,
  //   label: 'Mercedes G63',
  //   available: true
  // },
};

const Element = () => {
  const { classes } = useStyles();

  const {
    dataStore: { spaceStore },
    uiStore: { authStore },
  } = useStore();

  const loraOptions = getLoraOptons(authStore.currentUser?.id);

  // Always show Mercedes and BMW alongside user's options
  const allDisplayOptions = [
    ...loraOptions,
    // Add Mercedes and BMW if not already in loraOptions
    ...(loraOptions.find(opt => opt.value === 'mag_eqs') ? [] : [{ value: 'mag_eqs', label: 'Mercedes EQS (not yet available)' }]),
    ...(loraOptions.find(opt => opt.value === 'mag_bmw') ? [] : [{ value: 'mag_bmw', label: 'BMW (not yet available)' }])
  ];

  return (
    <Flex className={classes.wrapper}>
      <Text className={classes.title}>
        Select the car you want to integrate
      </Text>

      <Flex className={classes.carGrid}>
        {allDisplayOptions.map(item => {
          const carInfo = carImageMap[item.value];

          // Use carInfo if available, otherwise create default info
          const displayInfo = carInfo || {
            image: null,
            label: item.label,
            available: true
          };

          return (
            <Card
              key={item.value}
              className={classes.carCard}
              onClick={() => {
                if (displayInfo.available) {
                  spaceStore.setSecondLora({
                    label: item.label,
                    value: item.value
                  });
                }
              }}
              style={{
                opacity: displayInfo.available ? 1 : 0.6,
                cursor: displayInfo.available ? 'pointer' : 'not-allowed',
              }}
            >
              {displayInfo.image ? (
                <img
                  src={displayInfo.image}
                  alt={displayInfo.label}
                  className={classes.carImage}
                />
              ) : (
                <Box
                  className={classes.carImage}
                  style={{
                    backgroundColor: '#f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#999',
                    fontSize: '12px',
                  }}
                >
                  No Preview
                </Box>
              )}
              <Text className={classes.carLabel}>
                {displayInfo.label}
              </Text>
            </Card>
          );
        })}

        <Box
          className={classes.trainCard}
          onClick={() => {
            window.open('https://calendly.com/maground_com/ai-demo?preview_source=et_card', '_blank');
          }}
        >
          <Box className={classes.plusIcon}>
            +
          </Box>
          <Text className={classes.trainText}>
            Train your own Car Model
          </Text>
        </Box>
      </Flex>
    </Flex>
  );
};

export const CarLoraVariants = observer(Element);